import React, { FC, FormEvent, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import { Order } from 'shared/types/cart/Order';
import { mutate } from 'swr';
import Button from 'components/commercetools-ui/atoms/button';
import PasswordInput from 'components/commercetools-ui/atoms/input-password';
import Message from 'components/commercetools-ui/atoms/message';
import Typography from 'components/commercetools-ui/atoms/typography';
import { DataLayerHelpers } from 'helpers/dataLayerHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import useValidate from 'helpers/hooks/useValidate';
import { sdk } from 'sdk';
import { useAccount, useCart } from 'frontastic';
import { RegisterAccount } from 'frontastic/hooks/useAccount/types';
import useConfig from 'frontastic/hooks/useConfig';
import useCTStock from 'frontastic/hooks/useCTStock';
import { ImageProps } from 'frontastic/lib/image';
import ThankYouOrderInfo from './components/order-info';
import MarketingPreference from './components/order-marketing-preferences';
import PrintButton from './components/printButton';
import ThankYouFooter from './components/thank-you-footer';
import ThankYouHeader from './components/thank-you-header';
import OrderSummary from '../order-summary';
import { AlgoliaAnalytics } from 'helpers/AlgoliaEvents';
import { OBJECT_ID_QUERY_ID } from 'helpers/constants/algolia';

interface ThankYouContentProps {
  productLogo: ImageProps;
}

const ThankYouContent: FC<ThankYouContentProps> = ({ productLogo }) => {
  const { locale } = useLocale();
  const { formatMessage } = useFormat({ name: 'thank-you' });
  const searchParams = useSearchParams();
  const orderId = searchParams.get('orderId');
  const pushToDataLayer = searchParams.get('p2dl');
  const { validatePassword } = useValidate();
  const [pickup, setPickup] = useState('');
  const [preferenceData, setPreferenceData] = useState({
    isEmailSubscribed: true,
    isMailSubscribed: true,
    isSMSSubscribed: false,
    isPhoneSubscribed: false,
  });
  const [data, setData] = useState({
    password: '',
    confirmPassword: '',
    orderId: '',
    email: '',
  });
  const [error, setError] = useState({
    message: '',
    type: '',
  });
  const [isUserAlreadyRegistered, setConstantValue] = useState(true);
  const [cartMismatchError, setCartMismatchError] = useState(false);
  const [orderNotFoundError, setOrderNotFoundError] = useState(false);

  const [registerAccount, setRegisterAccount] = useState<RegisterAccount>({
    title: '',
    firstName: '',
    lastName: '',
    salutation: '',
    birthday: '',
    email: '',
    password: '',
    billingAddress: undefined,
    shippingAddress: undefined,
    phone: '',
  });

  const [loading, setLoading] = useState(false);

  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const [order, setOrder] = useState<Order | null>(null);

  const { getStores, getCheckoutOrder } = useCart();
  const { config } = useConfig();
  const { getStoreStock } = useCTStock();
  const { register, loggedIn, account, getDimension18, checkCustomer, result } = useAccount();
  const userId = loggedIn
    ? result?.data?.data?.account?.accountId
    : typeof window !== 'undefined' && localStorage?.getItem('__anon_id');
  const analytics = new AlgoliaAnalytics(locale);
  const initialViewTracked = useRef(false);

  const fetchStores = useCallback(
    async (targetStoreID: string, isCustomFit: boolean, isPersonalised: boolean, SKUs: string[], data: any) => {
      const storeData = await (getStores?.() ?? Promise.resolve([]));
      const storeIDs = storeData.map((store) => store.ID);
      const stocks = await getStoreStock(storeIDs, SKUs);
      const configObj = await config();
      const dispatchDate = new Date(data?.createdAt);

      const targetStore = storeData.find((store) => store.ID === targetStoreID);
      if (targetStore) {
        const stock = stocks.find((stock: any) => stock.storeRef === targetStore.ID);

        if (isCustomFit) {
          dispatchDate.setDate(dispatchDate.getDate() + configObj.customFitETAInDays);
          const estimated = dispatchDate.toLocaleDateString(locale.replace('_', '-'), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          setPickup(estimated);
          return;
        } else if (isPersonalised) {
          dispatchDate.setDate(dispatchDate.getDate() + configObj.personalisedProductETAInDays);
          const estimated = dispatchDate.toLocaleDateString(locale.replace('_', '-'), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
          setPickup(estimated);
          return;
        } else if (stock && stock.inStock) {
          const allProductsInStock = data?.lineItems?.every((item: any) => {
            const productStock = stock.productsStock.find((product: any) => product.sku === item.variant?.sku);
            return productStock && productStock.quantity >= (item.count as number);
          });

          if (allProductsInStock) {
            setPickup('2 hours');
            return;
          }
        }
      }
      setPickup('2 working days');
    },
    [getStores, getStoreStock, config],
  );

  const clearCart = useCallback(async () => {
    await sdk.callAction({ actionName: 'cart/resetCart' });
    mutate('/action/cart/getCart');
  }, []);

  const fetchOrderDetails = useCallback(async () => {
    if (!order || order.orderNumber !== orderId) {
      try {
        const res = await getCheckoutOrder(orderId as string);
        if ('message' in res && res.message === 'order-mismatch') {
          setCartMismatchError(true);
        } else if ('message' in res && res.message === 'order-not-found') {
          setOrderNotFoundError(true);
        }
        if (res.custom?.deliveryOption === 'StoreCollection') {
          await fetchStores(
            res.shippingAddress?.addressId || '',
            res.lineItems?.some((item) => item.isCustomFit) || false,
            res.lineItems?.some((item) => item.isPersonalized) || false,
            (res.lineItems?.map((item) => item.variant?.sku).filter((item) => item !== undefined) as string[]) || [],
            res,
          );
        }

        if (res.orderNumber && (res.orderId !== order?.orderId || !order)) {
          setOrder(res);
          const custom = res?.custom;
          const newPreferenceData = {
            isEmailSubscribed: custom?.isEmailSubscribed || true,
            isMailSubscribed: custom?.isMailSubscribed || true,
            isSMSSubscribed: custom?.isSMSSubscribed || false,
            isPhoneSubscribed: custom?.isPhoneSubscribed || false,
          };

          setPreferenceData({ ...newPreferenceData });

          setRegisterAccount({
            email: res?.email || '',
            password: '',
          });

          if (sessionStorage.getItem('pushToDataLayer') !== null) {
            let dimension18 = '';
            if (res.email) {
              dimension18 = await getDimension18(res.email);
            }
            const dataLayerData = {
              breadcrumb: 'Checkout / Confirmation',
              event: 'purchasePushed',
              dimension18: dimension18,
            };
            const orderUserEmail = loggedIn ? account?.email : res.email;
            const userEmail = loggedIn ? account?.email : '';
            const salutation = loggedIn ? account?.salutation || '' : '';
            const brandstakId = loggedIn ? account?.externalId || '' : '';
            const currentUrl = document.location.origin;
            const dataLayer = new DataLayerHelpers(
              currentUrl,
              locale,
              res,
              userEmail,
              account?.accountId,
              salutation,
              '',
              brandstakId,
              orderUserEmail,
            );
            dataLayer.setPurchaseDataLayer(dataLayerData);
            sessionStorage.removeItem('pushToDataLayer');
          }
          clearCart();
        }
      } catch (err) {
        console.error('Failed to fetch order details:', err);
      }
    }
    const fetchData = async () => {
      try {
        const data = await checkCustomer(registerAccount);
        setConstantValue(data.isCustomerExistInCT);
      } catch (error) {
        // Handle errors
      }
    };
    fetchData();
  }, [
    order,
    orderId,
    getCheckoutOrder,
    fetchStores,
    getDimension18,
    loggedIn,
    account,
    locale,
    isUserAlreadyRegistered,
  ]);

  useEffect(() => {
    if (pushToDataLayer) {
      const url = new URL(window.location.href);
      // Remove the 'p2dl' parameter
      url.searchParams.delete('p2dl');
      window.history.replaceState(null, '', url);
      sessionStorage.setItem('pushToDataLayer', 'true');
    }
    const intervalId = setInterval(fetchOrderDetails, 10000); // Poll every 10 seconds
    if (cartMismatchError || orderNotFoundError) {
      clearInterval(intervalId);
    }
    return () => clearInterval(intervalId);
  }, [fetchOrderDetails, cartMismatchError, orderNotFoundError]);

  const handlePrint = () => {
    window.print();
  };

  //handle text input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  async function handlePullAndRegister(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (!validatePassword(data?.password)) {
      setError({
        message: formatMessage({
          id: 'password.not.valid',
          defaultMessage: 'Password has to be at least 8 characters long and have at least one uppercase letter.',
        }),
        type: 'error',
      });
      return;
    }

    if (data?.password !== data?.confirmPassword) {
      setError({
        message: formatMessage({ id: 'password.mismatch', defaultMessage: 'Passwords must match.' }),
        type: 'error',
      });
      return;
    }

    setLoading(true);
    setData({ ...data, [data.orderId]: order?.orderNumber || '' });
    const address =
      order?.custom?.deliveryOption === 'HomeDelivery' || order?.custom?.deliveryOption === 'StoreCollection'
        ? order?.billingAddress
        : order?.shippingAddress;

    const registrationData = {
      email: order?.email || '',
      password: data?.password || '',
      title: address?.title || undefined,
      firstName: address?.firstName || undefined,
      lastName: address?.lastName || undefined,
      salutation: address?.salutation || undefined,
      birthday: '',
      billingAddress: order?.billingAddress || undefined,
      shippingAddress: order?.shippingAddress || undefined,
      phone: address?.phone || undefined,
      isMailSubscribed: preferenceData?.isEmailSubscribed,
      isEmailSubscribed: preferenceData?.isEmailSubscribed,
      isSMSSubscribed: preferenceData?.isSMSSubscribed,
      isPhoneSubscribed: preferenceData?.isPhoneSubscribed,
      anonymousId: order?.anonymousId,
    };
    try {
      const res = await register(registrationData);
      if (res.accountId) {
        setConstantValue(true);
        toast.success('Account successfully registered.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (e) {
      toast.error('Something went wrong while registering.');
    }
    setLoading(false);
    return;
  }

  // Process stored data when order is confirmed - order tracking event
  useEffect(() => {
    if (order?.orderId && !initialViewTracked?.current) {
      if (order?.lineItems) {
        const orderedItems = JSON?.parse(sessionStorage?.getItem(OBJECT_ID_QUERY_ID) || '[]');
        const objectIDs: string[] = [];
        const objectData: any[] = [];
        let totalValue = 0;

        for (const lineItem of order?.lineItems) {
          const variantCode = lineItem?.variant?.attributes?.variantCode?.replace(/[\s/]+/g, '_');

          // Match using variantCode
          const matchedItem = orderedItems?.find((item: string) => {
            const [id] = item?.split(':');
            return id === variantCode;
          });

          // if (matchedItem && variantCode) {
          const queryID = matchedItem?.split(':')?.pop();
          const quantity = lineItem?.count || 1;

          const originalPrice = lineItem?.price?.centAmount ? lineItem?.price?.centAmount / 100 : 0;
          const discountedPrice = lineItem?.discountedPrice?.centAmount
            ? lineItem?.discountedPrice?.centAmount / 100
            : originalPrice;

          // Sum all discounts in the array
          const totalItemDiscount = (lineItem?.discounts || [])?.reduce((sum, discount) => {
            return sum + (discount?.discountedAmount?.centAmount || 0) / 100;
          }, 0);

          const discountAmount = +(totalItemDiscount * quantity)?.toFixed(2);

          objectIDs?.push(variantCode);
          objectData?.push({
            queryID: queryID ?? undefined,
            price: discountedPrice,
            discount: discountAmount > 0 ? discountAmount : undefined,
            quantity,
          });

          totalValue += discountedPrice * quantity;
          // }
        }

        if (objectIDs?.length > 0) {
          analytics?.handleTrackCheckout(userId, objectIDs, objectData, totalValue, order?.sum?.currencyCode || 'USD');
          initialViewTracked.current = true;
          sessionStorage?.removeItem(OBJECT_ID_QUERY_ID);
        }
      }
    }
  }, [order?.orderId]);

  return (
    <div className="bg-neutral-200 lg:gap-26">
      {!cartMismatchError && !orderNotFoundError && (
        <div className="flex flex-col px-16 py-12 md:px-24 md:py-12 lg:flex-row lg:items-start lg:gap-26 lg:px-20 lg:py-48 xl:px-48">
          <div className="bg-white px-16 md:px-24 lg:w-[65%] lg:rounded-md lg:py-36">
            <ThankYouHeader
              deliveryOption={order?.custom?.deliveryOption}
              email={order?.email}
              onPrint={handlePrint}
              customerName={order?.billingAddress?.firstName}
              pickup={pickup}
              confirmed={account?.confirmed}
            />
            <ThankYouOrderInfo
              firstName={order?.shippingAddress?.firstName}
              lastName={order?.shippingAddress?.lastName}
              email={order?.email}
              order={order ?? undefined}
            />
            <ThankYouFooter
              deliveryOption={order?.custom?.deliveryOption}
              loading={!order?.sum}
              pickup={pickup}
              order={order}
            />
          </div>

          <div className="lg:w-[35%]">
            <OrderSummary
              className="bg-white px-16 pb-12 pt-24 md:px-24 md:py-12 lg:mt-0 lg:block lg:w-[100%] lg:p-36 lg:px-20 xl:px-48"
              title={formatMessage({ id: 'order.details', defaultMessage: 'Order details' })}
              order={order ?? undefined}
              productLogo={productLogo}
              dataReference="order"
              includeItemsList
              button={
                <PrintButton asSkeleton={!order?.sum} onPrint={handlePrint} className="mt-16 hidden w-full lg:block" />
              }
              classNames={{
                infoContainer: 'border-none p-0 pt-16',
                totalAmount:
                  'md:text-18 font-medium flex items-center justify-between lg:border-t lg:border-neutral-400 lg:pt-12',
              }}
            />
            {!loggedIn && !isUserAlreadyRegistered && (
              <div className="mt-30 bg-white py-40">
                <div className="m-auto mt-32 w-full px-16 md:max-w-[480px] md:px-24 lg:px-20 xl:px-48">
                  <Typography className="mb-20 scroll-mt-[300px] font-body text-20 font-bold uppercase md:text-23 xl:text-23">
                    {formatAccountMessage({ id: 'password.heading', defaultMessage: 'CREATE AN ACCOUNT' })}
                  </Typography>
                  <Typography className="font-normal">
                    {formatAccountMessage({
                      id: 'password.subheading',
                      defaultMessage: 'To complete your registration, simply provide a password.',
                    })}
                  </Typography>
                </div>
                <form
                  onSubmit={handlePullAndRegister}
                  noValidate
                  className="m-auto mt-24 w-full px-16 md:max-w-[480px] md:px-24 lg:px-20 xl:px-48"
                >
                  {error.message && <Message severity={error.type} message={error.message} />}
                  <PasswordInput
                    required
                    id="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder={formatAccountMessage({ id: 'password', defaultMessage: 'Password' })}
                    className="mb-16 md:mb-20"
                    onChange={handleChange}
                  />

                  <PasswordInput
                    required
                    id="confirm-password"
                    name="confirmPassword"
                    autoComplete="current-password"
                    placeholder={formatAccountMessage({ id: 'password.confirm', defaultMessage: 'Confirm Password' })}
                    className="mb-16 md:mb-20"
                    onChange={handleChange}
                  />
                  <Button
                    size="full"
                    type="submit"
                    className="mb-16 text-16 leading-tight md:mb-20"
                    disabled={loading}
                    loading={loading}
                  >
                    {formatAccountMessage({ id: 'account.register', defaultMessage: 'Register' })}
                  </Button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {cartMismatchError && (
        <div className="flex h-350 w-[450] flex-col items-center justify-start bg-white px-16 py-12 md:px-24 md:py-12 lg:px-20 lg:py-48 xl:px-48">
          <div className="lg:w-[850px]">
            <Message
              severity="info"
              message={formatAccountMessage({
                id: 'order.in.process',
                defaultMessage:
                  'Order processing.You will receive order updates to the email address provided when placing your order in due course.',
              })}
            />
          </div>
        </div>
      )}

      {orderNotFoundError && (
        <div className="flex h-350 w-[450] flex-col items-center justify-start bg-white px-16 py-12 md:px-24 md:py-12 lg:px-20 lg:py-48 xl:px-48">
          <div className="lg:w-[850px]">
            <Message
              severity="error"
              message={formatAccountMessage({
                id: 'order.not.found',
                defaultMessage: 'No Order Found. Please check your order details and try again.',
              })}
            />
          </div>
        </div>
      )}

      {!account && !cartMismatchError && !orderNotFoundError && (
        <MarketingPreference data={preferenceData} setPreferenceData={setPreferenceData} order={order ?? undefined} />
      )}
    </div>
  );
};

export default ThankYouContent;
